import React, { useEffect, useState } from "react";
import { TAM as TAMType } from "../../../models/auction";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { toSafeFloat } from "../../../extensions/number";
import { ChartAreaLoader } from "../../../components/Loading";
import { ExchangeColors } from "../Exchange3/FormatData";
import { COLOR_CODES, formatDMY } from "../../../common";
import { format } from "date-fns";
import { getTamMetrics } from "../../../Rest_api/RestApi";
import LegendSummaryTable, {
  ChartTable,
} from "../../../components/table/SummaryTable";
import Tooltip from "../../../components/tooltip";
import FilterMenu from "../../../components/FilterMenu";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import { useSelector } from "react-redux";
import MetricChip from "../../../components/Metrics";

interface TAMProps {
  TAMData: TAMType[];
  loading: boolean;
  startDate: Date;
  endDate: Date;
}

export const TAM: React.FC<TAMProps> = ({
  TAMData,
  loading,
  startDate,
  endDate,
}) => {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [selectTamExc, setSelectTamExc] = useState<string[]>([]);
  const [selectTamProduct, setSelectTamProduct] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [chartTabIndex, setChartTabIndex] = useState<number>(0);
  const [priceType, setPriceType] = useState<string[]>([]);
  const [selectedTamVariables, setSelectedTamVariables] = useState<string[]>(
    [],
  );
  const [filterLoading, setFilterLoading] = useState<boolean>(true);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);
  const [processedChartData, setProcessedChartData] = useState<any[]>([]);
  const [processedTableData, setProcessedTableData] = useState<any[]>([]);
  const [filteredTAMAuctionsList, setFilteredTAMAuctionsList] = useState<
    TAMType[]
  >([]);

  const chartProducts = [
    {
      product: "daily",
      name: "Daily",
      productName: "Daily",
      color: COLOR_CODES.daily,
    },
    {
      product: "intra",
      name: "Intra day",
      productName: "Intra day",
      color: COLOR_CODES.intraday,
    },
    {
      product: "contingency",
      name: "DAC",
      productName: "Day Ahead Contingency",
      color: COLOR_CODES.dac,
    },
    {
      product: "weekly",
      name: "Weekly",
      productName: "Weekly",
      color: COLOR_CODES.weekly,
    },

    {
      product: "monthly",
      name: "Monthly",
      productName: "Monthly",
      color: COLOR_CODES.monthly,
    },
    {
      product: "hp_daily",
      name: "HP Daily",
      productName: "Daily_HP",
      color: COLOR_CODES.hpDaily,
    },
    {
      product: "hp_intra",
      name: "HP Intra Day",
      productName: "IDayDynamic_HP",
      color: COLOR_CODES.hpIntraDay,
    },
    {
      product: "hp_contingency",
      name: "HP DAC",
      productName: "CONTINGENCY_HP",
      color: COLOR_CODES.hpDac,
    },
    {
      product: "hp_monthly",
      name: "HP Monthly",
      productName: "Monthly_HP",
      color: COLOR_CODES.hpMonthly,
    },
  ];
  const products = [
    {
      name: "DAC",
      key: "Day Ahead Contingency",
      keys: [
        "Day Ahead Contingency",
        "DACDynamic",
        "CONTINGENCY",
        "Contingency",
        "TAM(DAC)",
        "DACStatic",
        "DAC",
      ],
    },
    { name: "Daily", key: "Daily", keys: ["Daily", "DAILY"] },
    {
      name: "HP DAC",
      key: "DACDynamic_HP",
      keys: [
        "DACDynamic_HP",
        "DACStatic_TAM_H",
        "Day Ahead Contingency_HP",
        "DAY AHEAD CONTINGENCY_HP",
        "Contingency_HP",
        "DAY AHEAD CONTIGENCY_HP",
        "CONTINGENCY_HP",
        "HP DAC",
      ],
    },
    {
      name: "HP Daily",
      key: "Daily_HP",
      keys: ["Daily_HP", "DAILY_HP", "daily_hp", "HP Daily"],
    },
    {
      name: "HP Intra Day",
      key: "IDayDynamic_HP",
      keys: ["IDayDynamic_HP", "Intra Day_HP", "HP Intra Day"],
    },
    {
      name: "HP Monthly",
      key: "Monthly_HP",
      keys: ["Monthly_HP", "MONTHLY_HP", "monthly_hp", "HP Monthly"],
    },
    {
      name: "Intra day",
      key: "Intra day",
      keys: [
        "I_DayDynamic_C",
        "Intra day",
        "Intra Day",
        "I_DayStatic_C",
        "INTRA DAY",
      ],
    },
    { name: "Monthly", key: "Monthly", keys: ["Monthly"] },
    { name: "Weekly", key: "Weekly", keys: ["Weekly", "WEEKLY"] },
  ];

  const chartExchangeComprison = [
    "IEX_DAC",
    "HPX_DAC",
    "PXIL_DAC",
    "PXIL_Daily",
    "HPX_Daily",
    "PXIL_Intra day",
    "IEX_Intra day",
    "IEX_Daily",
    "HPX_Intra day",
    "HPX_Monthly",
    "PXIL_Monthly",
    "PXIL_Weekly",
    "IEX_Weekly",
    "HPX_Weekly",
    "IEX_Monthly",
    "IEX_HP Daily",
    "HPX_HP Daily",
    "PXIL_HP Daily",
    "PXIL_HP Intra Day",
    "IEX_HP Intra Day",
    "HPX_HP Intra Day",
    "IEX_HP Monthly",
    "HPX_HP Monthly",
    "PXIL_HP Monthly",
    "IEX_HP DAC",
    "HPX_HP DAC",
    "PXIL_HP DAC",

    // "PXIL_DACDynamic_HP",
    // "HPX_Daily_HP",
    // "PXIL_Daily_HP",
    // "PXIL_Monthly_HP",
    // "PXIL_IDayDynamic_HP",
    // "HPX_IDayDynamic_HP",
    // "HPX_DACDynamic_HP",
    // "IEX_DACDynamic_HP",
    // "IEX_IDayDynamic_HP",
    // "HPX_undefined",
    // "PXIL_undefined",
  ].map((value, index) => {
    const [exchange, exchangeProduct] = value.split("_");
    return {
      key: value,
      exchange,
      exchangeProduct,
      name: `${exchange} ${exchangeProduct}`,
      color: ExchangeColors[index + 1],
    };
  });
  const ITEMS_PER_PAGE = 100;

  const handleTAMSelectExc = (items: string[]) => {
    setSelectTamExc(items);
    setCurrentPage(1);
  };

  const getActiveExchangeComparison = (): {
    key: string;
    exchange: string;
    exchangeProduct: string;
    name: string;
    color: string;
  }[] => {
    // return chartExchangeComprison.filter((exchange) =>
    //   selectTamExc.includes(exchange.exchange)
    // );
    let data: any = [];
    chartExchangeComprison.forEach((exchange) => {
      if (
        (selectTamExc.includes(exchange.exchange) ||
          selectTamExc.length === 0) &&
        (selectTamProduct.includes(exchange.exchangeProduct) ||
          selectTamProduct.length == 0)
      ) {
        data.push(exchange);
      }
    });
    if (data.length == 0) {
      return chartExchangeComprison;
    }
    return data;
  };

  const handleTAMSelectProduct = (items: string[]) => {
    const newKeys = products
      .filter((p) => items.includes(p.name))
      .flatMap((p) => p.keys);
    setSelectTamProduct(newKeys);
    setCurrentPage(1);
  };

  const handlePriceType = (type: string) => {
    setPriceType((prev) =>
      prev.includes(type)
        ? prev.filter((item) => item !== type)
        : [...prev, type],
    );
    setCurrentPage(1);
  };

  const getTamVariableOptions = (): string[] => {
    if (chartTabIndex === 2) {
      return ["Total Traded Volume", "Wt. Avg Price"];
    }
    return ["Daily", "Daily Price", "Intra Day", "Intra Day Price"];
  };

  const filterTamLegendsByVariable = (legends: any[]): any[] => {
    if (selectedTamVariables.length === 0) return legends;
    return legends.filter((legend) => {
      const dk = (legend.dataKey as string).toLowerCase();
      if (chartTabIndex === 2) {
        if (
          selectedTamVariables.includes("Total Traded Volume") &&
          !legend.yAxisId
        )
          return true;
        if (
          selectedTamVariables.includes("Wt. Avg Price") &&
          legend.yAxisId === "right"
        )
          return true;
        return false;
      } else {
        if (
          selectedTamVariables.includes("Daily") &&
          dk.includes("daily") &&
          !legend.yAxisId
        )
          return true;
        if (
          selectedTamVariables.includes("Daily Price") &&
          dk.includes("daily") &&
          legend.yAxisId === "right"
        )
          return true;
        if (
          selectedTamVariables.includes("Intra Day") &&
          dk.includes("intra") &&
          !legend.yAxisId
        )
          return true;
        if (
          selectedTamVariables.includes("Intra Day Price") &&
          dk.includes("intra") &&
          legend.yAxisId === "right"
        )
          return true;
        return false;
      }
    });
  };

  const filteredTAMAuctions = () => {
    let resdate = TAMData;
    if (selectTamExc.length > 0) {
      resdate = resdate.filter((auction) =>
        selectTamExc.includes(auction.exchange!),
      );
    }
    if (selectTamProduct.length > 0) {
      resdate = resdate.filter((auction) =>
        selectTamProduct.includes(auction.product_gna!),
      );
    }

    resdate = resdate.map((auction) => ({
      ...auction,
      product: products.find((p) => p.keys.includes(auction.product_gna!))
        ?.key!,
    }));
    return resdate;
  };

  const paginatedTAMAuctions = filteredTAMAuctions().slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const getActiveChartProducts = (): {
    product: string;
    name: string;
    productName: string;
    color: string;
  }[] => {
    return selectTamProduct.length === 0
      ? chartProducts
      : chartProducts.filter((product) =>
          selectTamProduct.includes(product.productName),
        );
  };
  function handleNotFount(name: { name: string; key: string; keys: string[] }) {
    console.log(name);
    return name.name;
  }

  // const MAX_PRODUCTS = 4;

  const legendsForTable = [
    {
      dataKey: "total_traded_volume_mwh",
      name: "Volume(MWh)",
      // stroke: COLOR_CODES.primary,
    },
    {
      dataKey: "weighted_average_price_rs_mwh",
      name: "Wt. Avg Price(Rs/KWh)",
      // stroke: COLOR_CODES.secondary,
      yAxisId: "right",
    },
  ];

  const tablePar = {
    legends: legendsForTable,
    data: processedTableData,
    tableData: processedTableData,
    itemsPerPage: 100,
    unit: "MWh",
    extraKeys: [
      "instrument_name",
      "date",
      "exchange",
      "product",
      "no_of_trades",
    ],
    extraLabels: [
      "Instrument Name",
      "Date",
      "Exchange",
      "Contract Type",
      "No of Trades",
    ],
    filename: "TAM Data",
    showTotal: false,
    tableHeight: "calc(100vh - 315px)",
  };

  const [tamMetrics, setTamMetrics] = useState({
    totalVolume: 0,
    avgPrice: 0,
    totalTrades: 0,
  });

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const typeMapping = [
          "Compare Products",
          "Compare Exchanges",
          "Cumulative",
        ];
        const res = await getTamMetrics({
          start_date: format(startDate, "yyyy-MM-dd"),
          end_date: format(endDate, "yyyy-MM-dd"),
          exchange: selectTamExc.length > 0 ? selectTamExc : ["all"],
          type: typeMapping[chartTabIndex],
          variable: selectedTamVariables,
          product: selectTamProduct,
        });

        if (res) {
          setTamMetrics({
            totalVolume: res.total_traded_volume_mwh || 0,
            avgPrice: res.average_price_rs_mwh || 0,
            totalTrades: res.no_of_trades || 0,
          });
        }
      } catch (error) {
        console.error("Error fetching TAM metrics:", error);
      }
    };

    if (startDate && endDate) {
      fetchMetrics();
    }
  }, [
    startDate,
    endDate,
    selectTamExc,
    selectTamProduct,
    chartTabIndex,
    selectedTamVariables,
  ]);

  useEffect(() => {
    setFilterLoading(true);
    const timeoutId = setTimeout(() => {
      const activeAuctions = filteredTAMAuctions();
      setFilteredTAMAuctionsList(activeAuctions);
      setProcessedTableData(activeAuctions);

      if (chartTabIndex == 0) {
        setProcessedChartData(aggregateComparativeTAMData(activeAuctions));
      } else if (chartTabIndex == 1) {
        setProcessedChartData(
          aggregateExchangeComparativeTAMData(activeAuctions),
        );
      } else {
        setProcessedChartData(aggregateTAMData(activeAuctions) as any);
      }

      setFilterLoading(false);
      setHasInitialized(true);
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [TAMData, selectTamExc, selectTamProduct, chartTabIndex]);

  const cumulativeLegends: any[] = [
    {
      dataKey: "total_traded_volume_mwh",
      name: "Total Traded Volume (MWh)",
      type: 1,
      legendColor: "rgb(159, 159, 159)",
    },
    {
      dataKey: "weighted_average_price_rs_mwh",
      name: "Wt. Avg Price (Rs/KWh)",
      yAxisId: "right",
    },
  ];

  const productLegends: any[] = [
    ...getActiveChartProducts().map((product) => ({
      dataKey: `${product.product}_total_traded_volume_mwh`,
      name: product.name,
      type: 1,
    })),
    ...getActiveChartProducts().map((product) => ({
      dataKey: `${product.product}_weighted_average_price_rs_mwh`,
      name: `${product.name} Price`,
      yAxisId: "right",
    })),
  ];

  const exchangeLegends: any[] = [
    ...getActiveExchangeComparison().map((exchange) => ({
      dataKey: `${exchange.key}_total_mu`,
      name: exchange.name,
      type: 1,
    })),
    ...getActiveExchangeComparison().map((exchange) => ({
      dataKey: `${exchange.key}_weighted_avg_rs_per_kwh`,
      name: `${exchange.name} Price`,
      yAxisId: "right",
    })),
  ];

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Exchange"
            items={EXCHANGE_TYPES.filter((exchange) => exchange !== "DEEP")}
            selectedItems={
              selectTamExc.length > 0
                ? selectTamExc
                : EXCHANGE_TYPES.filter((exchange) => exchange !== "DEEP")
            }
            setSelectedItems={handleTAMSelectExc}
          />
          <FilterMenu
            title="Type"
            items={["Compare Products", "Compare Exchanges", "Cumulative"]}
            selectedItems={[
              ["Compare Products", "Compare Exchanges", "Cumulative"][
                chartTabIndex
              ],
            ]}
            onSelect={(_, index) => {
              setChartTabIndex(index);
              setSelectedTamVariables([]);
            }}
          />
          {/* <FilterMenu
            title="Variable"
            items={getTamVariableOptions()}
            selectedItems={
              selectedTamVariables.length > 0
                ? selectedTamVariables
                : getTamVariableOptions()
            }
            setSelectedItems={(items) => {
              setSelectedTamVariables(
                items.length === getTamVariableOptions().length ? [] : items
              );
            }}
          /> */}
          <FilterMenu
            title="Product"
            items={products.map((product) => product.name)}
            selectedItems={products
              .filter((product) => selectTamProduct.includes(product.key))
              .map((product) => product.name)}
            setSelectedItems={handleTAMSelectProduct}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader showViewMode />

        <div className="flex flex-wrap gap-4 my-0 justify-center">
          {[
            {
              label: "Total Traded Volume",
              value: tamMetrics.totalVolume,
              unit: "MWh",
            },
            {
              label: "Average Price",
              value: tamMetrics.avgPrice,
              unit: "Rs/MWh",
            },
            {
              label: "Number of Trades",
              value: tamMetrics.totalTrades,
              unit: "",
            },
          ].map((item) => (
            <MetricChip key={item.label} icon={null} items={[item]} />
          ))}
        </div>

        <div className="gas-chart-container" style={{ minHeight: 0, flex: 1 }}>
          <div className="gas-heading gap-x-10 w-full">
            <h2 className="chartHeading w-60 whitespace-nowrap">TAM</h2>
            <div className="flex gap-4 justify-end min-h-[38px] items-center flex-1 w-full"></div>
          </div>
          {loading ? (
            <ChartAreaLoader />
          ) : (
            <>
              {dataTab.label === "Chart" && (
                <div className="tamChart container-radius">
                  {chartTabIndex !== 1 ? (
                    <ReMixChart
                      barCategoryGap={2.6}
                      hidePrice={false}
                      onlyTitle={true}
                      isTimeSlot={false}
                      xaxisHeight={50}
                      unit="MWh"
                      data={processedChartData}
                      legends={filterTamLegendsByVariable(
                        chartTabIndex == 2 ? cumulativeLegends : productLegends,
                      )}
                      showBrush={processedChartData.length > 10}
                      brushIndex={{
                        startIndex: 0,
                        endIndex:
                          chartTabIndex == 0
                            ? Math.min(15, processedChartData.length - 1)
                            : Math.min(60, processedChartData.length - 1),
                      }}
                      xDataKey="date"
                      yAxisLabel="Total Traded Volume (MWh)"
                      secondYAxisLabel="Weighted Average Price (Rs/KWh)"
                      chartContainerHeight="calc(100vh - 200px)"
                    />
                  ) : (
                    <div className="raChart">
                      <ReMixChart
                        data={processedChartData}
                        xDataKey="date"
                        hidePrice={false}
                        onlyTitle={true}
                        unit="MWh"
                        yAxisLabel="Total Traded Volume (MWh)"
                        secondYAxisLabel="Weighted Average Price (Rs/KWh)"
                        showBrush={processedChartData.length > 10}
                        brushIndex={{
                          startIndex: 0,
                          endIndex: Math.min(60, processedChartData.length - 1),
                        }}
                        showSecondYAxis={true}
                        legends={filterTamLegendsByVariable(exchangeLegends)}
                        chartContainerHeight="calc(100vh - 200px)"
                      />
                    </div>
                  )}
                </div>
              )}
              {dataTab?.label === "Summary" && (
                <LegendSummaryTable {...(tablePar as any)} />
              )}
              {dataTab?.label === "Table" && (
                <ChartTable {...(tablePar as any)} />
              )}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<p>TAM</p>}
              text="1. IEX | HPX | PXIL TAM Market Data. 2. Products: DAC, Daily, HP DAC, HP Daily, HP Intra Day, HP Monthly, Intra Day, Monthly, Weekly "
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
};

const EXCHANGE_TYPES = ["IEX", "HPX", "PXIL"];

const aggregateTAMData = (tamData: TAMType[]): AggregatedTAM[] => {
  const aggregatedData: {
    [key: string]: { totalVolume: number; totalPrice: number; count: number };
  } = {};

  if (tamData.length === 0) return [];

  tamData.forEach((entry) => {
    if (
      entry.date &&
      entry.total_traded_volume_mwh !== null &&
      entry.weighted_average_price_rs_mwh !== null
    ) {
      const { date, total_traded_volume_mwh, weighted_average_price_rs_mwh } =
        entry;
      if (!aggregatedData[date]) {
        aggregatedData[date] = { totalVolume: 0, totalPrice: 0, count: 0 };
      }

      aggregatedData[date].totalVolume += toSafeFloat(
        total_traded_volume_mwh.toString(),
      );
      aggregatedData[date].totalPrice +=
        toSafeFloat(total_traded_volume_mwh.toString()) *
        toSafeFloat(weighted_average_price_rs_mwh.toString());
      aggregatedData[date].count += total_traded_volume_mwh;
    }
  });

  const result: AggregatedTAM[] = Object.keys(aggregatedData).map((date) => {
    const { totalVolume, totalPrice, count } = aggregatedData[date];
    return {
      date: date,
      total_traded_volume_mwh: totalVolume,
      weighted_average_price_rs_mwh:
        totalPrice === 0 ? 0 : totalPrice / totalVolume,
      average_weighted_price_rs_mwh: totalPrice === 0 ? 0 : totalPrice / count,
    };
  });

  // Sort by date ascending
  return result.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );
};

const aggregateComparativeTAMData = (tamData: TAMType[]): ComparativeTAM[] => {
  const aggregatedData: {
    [key: string]: ComparativeTAM;
  } = {};

  if (tamData.length === 0) return [];

  tamData.forEach((entry) => {
    if (
      entry.date &&
      entry.total_traded_volume_mwh !== null &&
      entry.weighted_average_price_rs_mwh !== null
    ) {
      const { date, total_traded_volume_mwh, weighted_average_price_rs_mwh } =
        entry;
      if (!aggregatedData[date]) {
        aggregatedData[date] = {
          date: date,
          total_traded_volume_mwh: 0,
          total_price_rs: 0,
          average_weighted_price_rs_mwh: 0,
          weighted_average_price_rs_mwh: 0,
          iex_total_traded_volume_mwh: 0,
          iex_total_price_rs: 0,
          iex_average_weighted_price_rs_mwh: 0,
          iex_weighted_average_price_rs_mwh: 0,
          hpx_total_traded_volume_mwh: 0,
          hpx_total_price_rs: 0,
          hpx_average_weighted_price_rs_mwh: 0,
          hpx_weighted_average_price_rs_mwh: 0,
          pxil_average_weighted_price_rs_mwh: 0,
          pxil_total_traded_volume_mwh: 0,
          pxil_total_price_rs: 0,
          pxil_weighted_average_price_rs_mwh: 0,
          daily_total_traded_volume_mwh: 0,
          daily_total_price_rs: 0,
          daily_average_weighted_price_rs_mwh: 0,
          daily_weighted_average_price_rs_mwh: 0,
          intra_total_traded_volume_mwh: 0,
          intra_total_price_rs: 0,
          intra_average_weighted_price_rs_mwh: 0,
          intra_weighted_average_price_rs_mwh: 0,
          contingency_total_traded_volume_mwh: 0,
          contingency_total_price_rs: 0,
          contingency_average_weighted_price_rs_mwh: 0,
          contingency_weighted_average_price_rs_mwh: 0,
          monthly_total_traded_volume_mwh: 0,
          monthly_total_price_rs: 0,
          monthly_average_weighted_price_rs_mwh: 0,
          monthly_weighted_average_price_rs_mwh: 0,
          weekly_total_traded_volume_mwh: 0,
          weekly_total_price_rs: 0,
          weekly_average_weighted_price_rs_mwh: 0,
          weekly_weighted_average_price_rs_mwh: 0,
          hp_daily_total_traded_volume_mwh: 0,
          hp_daily_total_price_rs: 0,
          hp_daily_average_weighted_price_rs_mwh: 0,
          hp_daily_weighted_average_price_rs_mwh: 0,
          hp_intra_total_traded_volume_mwh: 0,
          hp_intra_total_price_rs: 0,
          hp_intra_average_weighted_price_rs_mwh: 0,
          hp_intra_weighted_average_price_rs_mwh: 0,
          hp_contingency_total_traded_volume_mwh: 0,
          hp_contingency_total_price_rs: 0,
          hp_contingency_average_weighted_price_rs_mwh: 0,
          hp_contingency_weighted_average_price_rs_mwh: 0,
          hp_monthly_total_traded_volume_mwh: 0,
          hp_monthly_total_price_rs: 0,
          hp_monthly_average_weighted_price_rs_mwh: 0,
          hp_monthly_weighted_average_price_rs_mwh: 0,
        };
      }

      // ...existing aggregation logic...
      aggregatedData[date].total_traded_volume_mwh +=
        toSafeFloat(total_traded_volume_mwh.toString()) * 1;
      aggregatedData[date].total_price_rs +=
        toSafeFloat(total_traded_volume_mwh.toString()) *
        toSafeFloat(weighted_average_price_rs_mwh.toString()) *
        1;

      if (entry.exchange === "IEX") {
        aggregatedData[date].iex_total_traded_volume_mwh +=
          toSafeFloat(total_traded_volume_mwh.toString()) * 1;
        aggregatedData[date].iex_total_price_rs +=
          toSafeFloat(total_traded_volume_mwh.toString()) *
          toSafeFloat(weighted_average_price_rs_mwh.toString()) *
          1;
      }
      if (entry.exchange === "HPX") {
        aggregatedData[date].hpx_total_traded_volume_mwh +=
          toSafeFloat(total_traded_volume_mwh.toString()) * 1;
        aggregatedData[date].hpx_total_price_rs +=
          toSafeFloat(total_traded_volume_mwh.toString()) *
          toSafeFloat(weighted_average_price_rs_mwh.toString()) *
          1;
      }
      if (entry.exchange === "PXIL") {
        aggregatedData[date].pxil_total_traded_volume_mwh +=
          toSafeFloat(total_traded_volume_mwh.toString()) * 1;
        aggregatedData[date].pxil_total_price_rs +=
          toSafeFloat(total_traded_volume_mwh.toString()) *
          toSafeFloat(weighted_average_price_rs_mwh.toString()) *
          1;
      }

      // ...rest of the product aggregation logic...
      if (entry.product === "Intra day") {
        aggregatedData[date].intra_total_traded_volume_mwh +=
          toSafeFloat(total_traded_volume_mwh.toString()) * 1;
        aggregatedData[date].intra_total_price_rs +=
          toSafeFloat(total_traded_volume_mwh.toString()) *
          toSafeFloat(weighted_average_price_rs_mwh.toString()) *
          1;
      }

      if (entry.product === "Day Ahead Contingency") {
        aggregatedData[date].contingency_total_traded_volume_mwh +=
          toSafeFloat(total_traded_volume_mwh.toString()) * 1;
        aggregatedData[date].contingency_total_price_rs +=
          toSafeFloat(total_traded_volume_mwh.toString()) *
          toSafeFloat(weighted_average_price_rs_mwh.toString()) *
          1;
      }

      if (entry.product === "Monthly") {
        aggregatedData[date].monthly_total_traded_volume_mwh +=
          toSafeFloat(total_traded_volume_mwh.toString()) * 1;
        aggregatedData[date].monthly_total_price_rs +=
          toSafeFloat(total_traded_volume_mwh.toString()) *
          toSafeFloat(weighted_average_price_rs_mwh.toString()) *
          1;
      }

      if (entry.product === "Daily") {
        aggregatedData[date].daily_total_traded_volume_mwh +=
          toSafeFloat(total_traded_volume_mwh.toString()) * 1;
        aggregatedData[date].daily_total_price_rs +=
          toSafeFloat(total_traded_volume_mwh.toString()) *
          toSafeFloat(weighted_average_price_rs_mwh.toString()) *
          1;
      }
      if (entry.product === "Weekly") {
        aggregatedData[date].weekly_total_traded_volume_mwh +=
          toSafeFloat(total_traded_volume_mwh.toString()) * 1;
        aggregatedData[date].weekly_total_price_rs +=
          toSafeFloat(total_traded_volume_mwh.toString()) *
          toSafeFloat(weighted_average_price_rs_mwh.toString()) *
          1;
      }
      if (entry.product === "Daily_HP") {
        aggregatedData[date].hp_daily_total_traded_volume_mwh +=
          toSafeFloat(total_traded_volume_mwh.toString()) * 1;
        aggregatedData[date].hp_daily_total_price_rs +=
          toSafeFloat(total_traded_volume_mwh.toString()) *
          toSafeFloat(weighted_average_price_rs_mwh.toString()) *
          1;
      }
      if (entry.product === "IDayDynamic_HP") {
        aggregatedData[date].hp_intra_total_traded_volume_mwh +=
          toSafeFloat(total_traded_volume_mwh.toString()) * 1;
        aggregatedData[date].hp_intra_total_price_rs +=
          toSafeFloat(total_traded_volume_mwh.toString()) *
          toSafeFloat(weighted_average_price_rs_mwh.toString()) *
          1;
      }
      if (entry.product === "DACDynamic_HP") {
        aggregatedData[date].hp_contingency_total_traded_volume_mwh +=
          toSafeFloat(total_traded_volume_mwh.toString()) * 1;
        aggregatedData[date].hp_contingency_total_price_rs +=
          toSafeFloat(total_traded_volume_mwh.toString()) *
          toSafeFloat(weighted_average_price_rs_mwh.toString()) *
          1;
      }
      if (entry.product === "Monthly_HP") {
        aggregatedData[date].hp_monthly_total_traded_volume_mwh +=
          toSafeFloat(total_traded_volume_mwh.toString()) * 1;
        aggregatedData[date].hp_monthly_total_price_rs +=
          toSafeFloat(total_traded_volume_mwh.toString()) *
          toSafeFloat(weighted_average_price_rs_mwh.toString()) *
          1;
      }
    }
  });

  const result: ComparativeTAM[] = Object.keys(aggregatedData).map((date) => {
    const {
      total_traded_volume_mwh,
      total_price_rs,
      daily_total_traded_volume_mwh,
      daily_total_price_rs,
      intra_total_traded_volume_mwh,
      intra_total_price_rs,
      contingency_total_traded_volume_mwh,
      contingency_total_price_rs,
      monthly_total_traded_volume_mwh,
      monthly_total_price_rs,
    } = aggregatedData[date];
    return {
      date,
      total_traded_volume_mwh: toSafeFloat(total_traded_volume_mwh),
      total_price_rs: toSafeFloat(total_price_rs),
      average_weighted_price_rs_mwh: toSafeFloat(
        total_price_rs / total_traded_volume_mwh,
      ),
      weighted_average_price_rs_mwh: toSafeFloat(
        total_price_rs / total_traded_volume_mwh,
      ),
      iex_total_traded_volume_mwh: toSafeFloat(
        aggregatedData[date].iex_total_traded_volume_mwh,
      ),
      iex_total_price_rs: toSafeFloat(aggregatedData[date].iex_total_price_rs),
      iex_average_weighted_price_rs_mwh: toSafeFloat(
        aggregatedData[date].iex_total_price_rs /
          aggregatedData[date].iex_total_traded_volume_mwh,
      ),
      iex_weighted_average_price_rs_mwh: toSafeFloat(
        aggregatedData[date].iex_total_price_rs /
          aggregatedData[date].iex_total_traded_volume_mwh,
      ),
      hpx_total_traded_volume_mwh: toSafeFloat(
        aggregatedData[date].hpx_total_traded_volume_mwh,
      ),
      hpx_total_price_rs: toSafeFloat(aggregatedData[date].hpx_total_price_rs),
      hpx_average_weighted_price_rs_mwh: toSafeFloat(
        aggregatedData[date].hpx_total_price_rs /
          aggregatedData[date].hpx_total_traded_volume_mwh,
      ),
      hpx_weighted_average_price_rs_mwh: toSafeFloat(
        aggregatedData[date].hpx_total_price_rs /
          aggregatedData[date].hpx_total_traded_volume_mwh,
      ),
      pxil_total_traded_volume_mwh: toSafeFloat(
        aggregatedData[date].pxil_total_traded_volume_mwh,
      ),
      pxil_total_price_rs: toSafeFloat(
        aggregatedData[date].pxil_total_price_rs,
      ),
      pxil_average_weighted_price_rs_mwh: toSafeFloat(
        aggregatedData[date].pxil_total_price_rs /
          aggregatedData[date].pxil_total_traded_volume_mwh,
      ),
      pxil_weighted_average_price_rs_mwh: toSafeFloat(
        aggregatedData[date].pxil_total_price_rs /
          aggregatedData[date].pxil_total_traded_volume_mwh,
      ),
      daily_total_traded_volume_mwh: toSafeFloat(daily_total_traded_volume_mwh),
      daily_total_price_rs: toSafeFloat(daily_total_price_rs),
      daily_average_weighted_price_rs_mwh: toSafeFloat(
        daily_total_price_rs / daily_total_traded_volume_mwh,
      ),
      daily_weighted_average_price_rs_mwh: toSafeFloat(
        daily_total_price_rs / daily_total_traded_volume_mwh,
      ),
      intra_total_traded_volume_mwh: toSafeFloat(intra_total_traded_volume_mwh),
      intra_total_price_rs: toSafeFloat(intra_total_price_rs),
      intra_average_weighted_price_rs_mwh: toSafeFloat(
        intra_total_price_rs / intra_total_traded_volume_mwh,
      ),
      intra_weighted_average_price_rs_mwh: toSafeFloat(
        intra_total_price_rs / intra_total_traded_volume_mwh,
      ),
      contingency_total_traded_volume_mwh: toSafeFloat(
        contingency_total_traded_volume_mwh,
      ),
      contingency_total_price_rs: toSafeFloat(contingency_total_price_rs),
      contingency_average_weighted_price_rs_mwh: toSafeFloat(
        contingency_total_price_rs / contingency_total_traded_volume_mwh,
      ),
      contingency_weighted_average_price_rs_mwh: toSafeFloat(
        contingency_total_price_rs / contingency_total_traded_volume_mwh,
      ),
      monthly_total_traded_volume_mwh: toSafeFloat(
        monthly_total_traded_volume_mwh,
      ),
      monthly_total_price_rs: toSafeFloat(monthly_total_price_rs),
      monthly_average_weighted_price_rs_mwh: toSafeFloat(
        monthly_total_price_rs / monthly_total_traded_volume_mwh,
      ),
      monthly_weighted_average_price_rs_mwh: toSafeFloat(
        monthly_total_price_rs / monthly_total_traded_volume_mwh,
      ),
      weekly_total_traded_volume_mwh: toSafeFloat(
        aggregatedData[date].weekly_total_traded_volume_mwh,
      ),
      weekly_total_price_rs: toSafeFloat(
        aggregatedData[date].weekly_total_price_rs,
      ),
      weekly_average_weighted_price_rs_mwh: toSafeFloat(
        aggregatedData[date].weekly_total_price_rs /
          aggregatedData[date].weekly_total_traded_volume_mwh,
      ),
      weekly_weighted_average_price_rs_mwh: toSafeFloat(
        aggregatedData[date].weekly_total_price_rs /
          aggregatedData[date].weekly_total_traded_volume_mwh,
      ),
      hp_daily_total_traded_volume_mwh: toSafeFloat(
        aggregatedData[date].hp_daily_total_traded_volume_mwh,
      ),
      hp_daily_total_price_rs: toSafeFloat(
        aggregatedData[date].hp_daily_total_price_rs,
      ),
      hp_daily_average_weighted_price_rs_mwh: toSafeFloat(
        aggregatedData[date].hp_daily_total_price_rs /
          aggregatedData[date].hp_daily_total_traded_volume_mwh,
      ),
      hp_daily_weighted_average_price_rs_mwh: toSafeFloat(
        aggregatedData[date].hp_daily_total_price_rs /
          aggregatedData[date].hp_daily_total_traded_volume_mwh,
      ),
      hp_intra_total_traded_volume_mwh: toSafeFloat(
        aggregatedData[date].hp_intra_total_traded_volume_mwh,
      ),
      hp_intra_total_price_rs: toSafeFloat(
        aggregatedData[date].hp_intra_total_price_rs,
      ),
      hp_intra_average_weighted_price_rs_mwh: toSafeFloat(
        aggregatedData[date].hp_intra_total_price_rs /
          aggregatedData[date].hp_intra_total_traded_volume_mwh,
      ),
      hp_intra_weighted_average_price_rs_mwh: toSafeFloat(
        aggregatedData[date].hp_intra_total_price_rs /
          aggregatedData[date].hp_intra_total_traded_volume_mwh,
      ),
      hp_contingency_total_traded_volume_mwh: toSafeFloat(
        aggregatedData[date].hp_contingency_total_traded_volume_mwh,
      ),
      hp_contingency_total_price_rs: toSafeFloat(
        aggregatedData[date].hp_contingency_total_price_rs,
      ),
      hp_contingency_average_weighted_price_rs_mwh: toSafeFloat(
        aggregatedData[date].hp_contingency_total_price_rs /
          aggregatedData[date].hp_contingency_total_traded_volume_mwh,
      ),
      hp_contingency_weighted_average_price_rs_mwh: toSafeFloat(
        aggregatedData[date].hp_contingency_total_price_rs /
          aggregatedData[date].hp_contingency_total_traded_volume_mwh,
      ),
      hp_monthly_total_traded_volume_mwh: toSafeFloat(
        aggregatedData[date].hp_monthly_total_traded_volume_mwh,
      ),
      hp_monthly_total_price_rs: toSafeFloat(
        aggregatedData[date].hp_monthly_total_price_rs,
      ),
      hp_monthly_average_weighted_price_rs_mwh: toSafeFloat(
        aggregatedData[date].hp_monthly_total_price_rs /
          aggregatedData[date].hp_monthly_total_traded_volume_mwh,
      ),
      hp_monthly_weighted_average_price_rs_mwh: toSafeFloat(
        aggregatedData[date].hp_monthly_total_price_rs /
          aggregatedData[date].hp_monthly_total_traded_volume_mwh,
      ),
    };
  });

  // Sort by date ascending
  return result.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );
};

interface AggregatedTAM {
  date: string;
  total_traded_volume_mwh: number;
  average_weighted_price_rs_mwh: number;
  weighted_average_price_rs_mwh: number;
}

interface ComparativeTAM {
  date: string;
  total_traded_volume_mwh: number;
  total_price_rs: number;
  average_weighted_price_rs_mwh: number;
  weighted_average_price_rs_mwh: number;
  iex_total_traded_volume_mwh: number;
  iex_total_price_rs: number;
  iex_average_weighted_price_rs_mwh: number;
  iex_weighted_average_price_rs_mwh: number;
  hpx_total_traded_volume_mwh: number;
  hpx_total_price_rs: number;
  hpx_average_weighted_price_rs_mwh: number;
  hpx_weighted_average_price_rs_mwh: number;
  pxil_total_traded_volume_mwh: number;
  pxil_total_price_rs: number;
  pxil_average_weighted_price_rs_mwh: number;
  pxil_weighted_average_price_rs_mwh: number;

  daily_total_traded_volume_mwh: number;
  daily_total_price_rs: number;
  daily_average_weighted_price_rs_mwh: number;
  daily_weighted_average_price_rs_mwh: number;
  intra_total_traded_volume_mwh: number;
  intra_total_price_rs: number;
  intra_average_weighted_price_rs_mwh: number;
  intra_weighted_average_price_rs_mwh: number;
  contingency_total_traded_volume_mwh: number;
  contingency_total_price_rs: number;
  contingency_average_weighted_price_rs_mwh: number;
  contingency_weighted_average_price_rs_mwh: number;
  monthly_total_traded_volume_mwh: number;
  monthly_total_price_rs: number;
  monthly_average_weighted_price_rs_mwh: number;
  monthly_weighted_average_price_rs_mwh: number;

  weekly_total_traded_volume_mwh: number;
  weekly_total_price_rs: number;
  weekly_average_weighted_price_rs_mwh: number;
  weekly_weighted_average_price_rs_mwh: number;
  hp_daily_total_traded_volume_mwh: number;
  hp_daily_total_price_rs: number;
  hp_daily_average_weighted_price_rs_mwh: number;
  hp_daily_weighted_average_price_rs_mwh: number;
  hp_intra_total_traded_volume_mwh: number;
  hp_intra_total_price_rs: number;
  hp_intra_average_weighted_price_rs_mwh: number;
  hp_intra_weighted_average_price_rs_mwh: number;
  hp_contingency_total_traded_volume_mwh: number;
  hp_contingency_total_price_rs: number;
  hp_contingency_average_weighted_price_rs_mwh: number;
  hp_contingency_weighted_average_price_rs_mwh: number;
  hp_monthly_total_traded_volume_mwh: number;
  hp_monthly_total_price_rs: number;
  hp_monthly_average_weighted_price_rs_mwh: number;
  hp_monthly_weighted_average_price_rs_mwh: number;
}

interface CompareTamExchange {
  [key: string]: any;
}

const aggregateExchangeComparativeTAMData = (
  tamData: TAMType[],
): CompareTamExchange[] => {
  let unitCombinations: string[] = [];
  tamData.forEach((entry) => {
    if (!unitCombinations.includes(`${entry.exchange}_${entry.product_gna}`)) {
      unitCombinations.push(`${entry.exchange}_${entry.product_gna}`);
    }
  });

  const aggregatedData: {
    [key: string]: CompareTamExchange;
  } = {};

  if (tamData.length === 0) return [];
  let date = new Date(formatDMY(tamData[0].date!) as any);
  date = new Date(date.setDate(date.getDate() - 1));
  const start_date = new Date(tamData[0].date as any);
  const end_date = new Date(
    formatDMY(tamData[tamData.length - 1].date!) as any,
  );
  while (date.getTime() <= end_date.getTime()) {
    const dateString = formatDMY(date.toISOString().split("T")[0]);
    aggregatedData[dateString] = {};

    unitCombinations.forEach((unit) => {
      const [exchange, product] = unit.split("_");
      aggregatedData[dateString][`${exchange}_${product}_total_mcv`] = 0;
      aggregatedData[dateString][`${exchange}_${product}_total_mu`] = 0;
      aggregatedData[dateString][`${exchange}_${product}_total_price`] = 0;
      aggregatedData[dateString][
        `${exchange}_${product}_weighted_avg_rs_per_kwh`
      ] = 0;
    });

    date.setDate(date.getDate() + 1);
    date = new Date(date); // Create a new date object to avoid modifying the original date object
  }

  tamData.forEach((entry) => {
    entry.date = entry.date;

    if (
      entry.date &&
      entry.total_traded_volume_mwh !== null &&
      entry.weighted_average_price_rs_mwh !== null
    ) {
      const { date, total_traded_volume_mwh, weighted_average_price_rs_mwh } =
        entry;
      if (!aggregatedData[date]) {
        aggregatedData[date] = {
          date: date,
        };
        unitCombinations.forEach((unit) => {
          const [exchange, product] = unit.split("_");
          aggregatedData[date][`${exchange}_${product}_total_mcv`] = 0;
          aggregatedData[date][`${exchange}_${product}_total_mu`] = 0;
          aggregatedData[date][`${exchange}_${product}_total_price`] = 0;
          aggregatedData[date][
            `${exchange}_${product}_weighted_avg_rs_per_kwh`
          ] = 0;
        });
      }

      const unit = `${entry.exchange}_${entry.product_gna}`;
      if (!unitCombinations.includes(unit)) {
      }
      aggregatedData[date][`${unit}_total_mcv`] +=
        toSafeFloat(total_traded_volume_mwh.toString()) * toSafeFloat(1);
      aggregatedData[date][`${unit}_total_mu`] +=
        toSafeFloat(total_traded_volume_mwh.toString()) * 1;
      aggregatedData[date][`${unit}_total_price`] +=
        toSafeFloat(total_traded_volume_mwh.toString()) *
        toSafeFloat(weighted_average_price_rs_mwh.toString()) *
        1;
      aggregatedData[date][`${unit}_weighted_avg_rs_per_kwh`] = toSafeFloat(
        aggregatedData[date][`${unit}_total_price`] /
          aggregatedData[date][`${unit}_total_mcv`],
      );
    }
  });

  const result: CompareTamExchange[] = Object.keys(aggregatedData).map(
    (date) => {
      const data = aggregatedData[date];
      return {
        date,
        ...data,
      };
    },
  );
  // remove the first element
  result.shift();
  return result;
};
